import React from 'react';
import PropTypes from 'prop-types';
import { Checkbox, Select, SelectContent, SelectGroup, SelectItem, SelectLabel } from '@dextech-ai/dex-design-system';
import { filterByLabel } from '@/utils/select-items';
import { OPERATORS, getAvailableOperators } from './operators';
import DanglingRefRow from './DanglingRefRow';

/**
 * Render addressable field entries as grouped Select options. Fields sharing a
 * top-level parent group under a single SelectGroup for readability in deep
 * schemas. Paths that ARE a top-level field (length-1) render ungrouped at
 * the bottom of the group (the group label matches the entry itself).
 */
function renderGroupedFieldOptions(fields) {
	// Preserve input order (paths.js already yields container before children).
	const groups = new Map();
	for (const f of fields) {
		const groupKey = f.topLevel || f.displayPath;
		if (!groups.has(groupKey)) groups.set(groupKey, []);
		groups.get(groupKey).push(f);
	}
	const out = [];
	for (const [groupKey, entries] of groups.entries()) {
		// If group has only one entry, render a bare option (no group overhead).
		if (entries.length === 1) {
			const f = entries[0];
			out.push(
				<SelectItem key={f.displayPath} value={f.displayPath}>
					{f.displayPath}
				</SelectItem>
			);
			continue;
		}
		out.push(
			<SelectGroup key={`grp-${groupKey}`}>
				<SelectLabel>{groupKey}</SelectLabel>
				{entries.map((f) => (
					<SelectItem key={f.displayPath} value={f.displayPath}>
						{f.displayPath}
					</SelectItem>
				))}
			</SelectGroup>
		);
	}
	return out;
}

/**
 * One row of an IF clause: base-field selector + operator + operator-specific
 * value widget. The operator Select is filtered by the selected field's
 * type — string-like fields only get const/enum/not_in/type_is_null, numeric
 * fields additionally get min/max/exclusive bounds, array fields get
 * contains_const. Changing the field resets the operator to the first
 * compatible one.
 */
export default function IfConditionRow({
	condition,
	condIndex,
	ruleIndex,
	availableFields,
	errors,
	onChange,
	onRemove,
}) {
	const testIdBase = `xallof-rule-${ruleIndex}-if-cond-${condIndex}`;
	const fieldMeta = availableFields.find(
		(f) => f.displayPath === condition.field
	);
	const fieldType = fieldMeta?.dataType || 'string';

	if (condition.field && !fieldMeta) {
		return (
			<DanglingRefRow
				scope="if-cond"
				ruleIndex={ruleIndex}
				condIndex={condIndex}
				missingName={condition.field}
				compatibleFields={availableFields}
				onReplace={(newName) => onChange({ ...condition, field: newName })}
				onRemove={onRemove}
			/>
		);
	}

	const operators = getAvailableOperators(fieldType);
	const operatorMeta = OPERATORS[condition.operator] || OPERATORS.const;
	const widget = operatorMeta.widget;

	const errorMsgs = errors || [];
	const errId = errorMsgs.length > 0 ? `${testIdBase}-err` : undefined;

	const handleFieldChange = (next) => {
		const nextFieldMeta = availableFields.find((f) => f.displayPath === next);
		const nextOps = getAvailableOperators(nextFieldMeta?.dataType || 'string');
		const keepOp = nextOps.includes(condition.operator) ? condition.operator : nextOps[0];
		onChange({ ...condition, field: next, operator: keepOp, value: '' });
	};

	const handleOperatorChange = (nextOp) => {
		const keepValue = OPERATORS[nextOp]?.needsValue
			&& OPERATORS[condition.operator]?.needsValue
			? condition.value
			: '';
		onChange({ ...condition, operator: nextOp, value: keepValue });
	};

	const handleValueChange = (v) => {
		onChange({ ...condition, value: v });
	};

	const handleRequiredChange = (checked) => {
		onChange({ ...condition, required: checked });
	};

	return (
		<div
			className={`dsv-xallof-builder__if-cond-row${errorMsgs.length > 0 ? ' dsv-xallof-builder__if-cond-row--invalid' : ''}`}
			role="group"
			aria-label={`IF condition ${condIndex + 1}`}
			data-testid={`${testIdBase}`}
		>
			<div className="dsv-xallof-builder__if-cond-grid">
				<div className="dsv-xallof-builder__select">
					<Select
						id={`${testIdBase}-field`}
						size="md"
						value={condition.field || null}
						onChange={handleFieldChange}
						placeholder="Select field…"
						showSearch
						filterOption={filterByLabel}
						popupMatchSelectWidth={false}
						aria-label="Field"
						aria-describedby={errId}
						data-testid={`${testIdBase}-field`}
					>
						<SelectContent>{renderGroupedFieldOptions(availableFields)}</SelectContent>
					</Select>
				</div>

				<div className="dsv-xallof-builder__select dsv-xallof-builder__select--op">
					<Select
						id={`${testIdBase}-operator`}
						size="md"
						value={condition.operator || 'const'}
						onChange={handleOperatorChange}
						aria-label="Operator"
						data-testid={`${testIdBase}-operator`}
					>
						<SelectContent>
							{operators.map((op) => (
								<SelectItem key={op} value={op}>{OPERATORS[op].label}</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>

				<div className="dsv-xallof-builder__value-slot">
					{widget === 'text' && (
						<input
							id={`${testIdBase}-value`}
							className="dsv-xallof-builder__control"
							type="text"
							value={condition.value ?? ''}
							onChange={(e) => handleValueChange(e.target.value)}
							placeholder="value"
							aria-label="Value"
							aria-describedby={errId}
							data-testid={`${testIdBase}-value`}
						/>
					)}
					{widget === 'csv' && (
						<input
							id={`${testIdBase}-value`}
							className="dsv-xallof-builder__control"
							type="text"
							value={Array.isArray(condition.value)
								? condition.value.join(', ')
								: (condition.value ?? '')}
							onChange={(e) => handleValueChange(e.target.value)}
							placeholder="a, b, c"
							aria-label="Values (comma separated)"
							aria-describedby={errId}
							data-testid={`${testIdBase}-value`}
						/>
					)}
					{widget === 'number' && (
						<input
							id={`${testIdBase}-value`}
							className="dsv-xallof-builder__control"
							type="number"
							value={condition.value ?? ''}
							onChange={(e) => handleValueChange(e.target.value)}
							placeholder="number"
							aria-label="Value"
							aria-describedby={errId}
							data-testid={`${testIdBase}-value`}
						/>
					)}
					{widget === 'none' && (
						<span className="dsv-xallof-builder__hint dsv-xallof-builder__value-slot--empty">
							no value needed
						</span>
					)}
				</div>

				<Checkbox
					className="dsv-xallof-builder__required-toggle"
					title="Adds this field to the if.required list"
					checked={condition.required !== false}
					onCheckedChange={handleRequiredChange}
					data-testid={`${testIdBase}-required`}
				>
					required
				</Checkbox>

				<button
					type="button"
					className="dsv-xallof-builder__icon-btn dsv-xallof-builder__icon-btn--danger"
					onClick={onRemove}
					aria-label={`Remove condition ${condIndex + 1}`}
					title="Remove condition"
					data-testid={`${testIdBase}-delete`}
				>
					×
				</button>
			</div>

			{errorMsgs.length > 0 && (
				<span className="dsv-xallof-builder__error" id={errId}>
					{errorMsgs.join(' ')}
				</span>
			)}
		</div>
	);
}

IfConditionRow.propTypes = {
	condition: PropTypes.object.isRequired,
	condIndex: PropTypes.number.isRequired,
	ruleIndex: PropTypes.number.isRequired,
	availableFields: PropTypes.array.isRequired,
	errors: PropTypes.arrayOf(PropTypes.string),
	onChange: PropTypes.func.isRequired,
	onRemove: PropTypes.func.isRequired,
};
